import { yupResolver } from '@hookform/resolvers/yup';
import { paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import {
  AddOnRateType,
  getV1CompaniesCompanyIdAddOnsTypeahead,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import {
  nameIdNonRequiredSchema,
  NameIdSchemaRequired,
  nameIdSchemaRequired,
} from '~constants/regexConst';
import { AddOn, AddOnAssignment, AddOnTypeahead } from '~hooks/useAddOns/models';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { useFuelSurchargeSchedule } from '~hooks/useFuelSurchargeSchedule/useFuelSurchargeSchedule';
import { testUniqueAddOnAssignments } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import { RemoveItemButton } from '~pages/Sales/sharedSales/RemoveItemButton';
import AddOnForm, {
  AddOnFormHandler,
} from '~pages/Settings/RatesManagement/AddOnCharges/components/AddOnForm';
import { addOnTypeOptions } from '~pages/Settings/RatesManagement/AddOnCharges/components/addOnFormSchema';
import {
  AddOnDialogState,
  getAddOnRateTypeOptionLabel,
} from '~pages/Settings/RatesManagement/AutomaticSettlementAddOns/components/AutomaticAddOns';
import { ROUTES } from '~router/routePaths';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const addOnRateTypeOptionsList = Object.values(AddOnRateType).filter(
  (rateType) => rateType !== AddOnRateType.FUEL_SURCHARGE,
);

const constructEmptyAddOn = () => ({
  id: '',
  name: '',
  rateType: '',
  quantity: 0,
  rate: 0,
  percentage: 0,
  rateValue: 0,
  costRateValue: 0,
  addOnRateType: '',
  externalId: '',
  accountingId: '',
  totalInstallments: 0,
  addOnTypes: addOnTypeOptions,
  addOnId: '',
  fuelSurchargeSchedule: {
    id: '',
    name: '',
  },
});

export const addOnSchema = yup
  .object()
  .shape({
    id: yup.string().notRequired(),
    name: yup.string(),
    rateType: yup.string(),
    quantity: yup.number(),
    percentage: yup.number(),
    rateValue: yup.number(),
    costRateValue: yup.number().notRequired(),
    externalId: yup.string().notRequired(),
    accountingId: yup.string().notRequired(),
    totalInstallments: yup.number().integer().min(0),
    addOnTypes: yup
      .array()
      .of(nameIdSchemaRequired)
      .min(
        1,
        `${t('form_validation_errors.min_count_one', { field: t('administration.rates.add_ons.add_on_types'), count: 1 })}`,
      )
      .notRequired(),
    addOnId: yup.string().notRequired(),
    fuelSurchargeSchedule: nameIdNonRequiredSchema.notRequired(),
  })
  .test('id-or-addonid', '', function (value) {
    return Boolean(value.id || value.addOnId) || this.parent.isDeleted;
  });

const addOnAssignmentSchema = yup
  .object()
  .shape({
    isDeleted: yup.boolean().notRequired(),
    // Distinguishes freshly-added rows (need POST) from rows loaded from the
    // BE (need PATCH). RatesManagement.tsx used to infer this from `addOnId`
    // being empty, which prevented the dedup test from comparing existing
    // rows against newly-added ones. Carrying an explicit flag lets us
    // populate `addOnId` for everything (so dedup works) while keeping the
    // save logic correct.
    isNew: yup.boolean().notRequired(),
    addOn: addOnSchema.notRequired(),
  })
  .required();

const automaticAddOnsSchema = yup.object().shape({
  addOns: yup
    .array()
    .of(addOnAssignmentSchema)
    .required()
    .test('unique-add-on-assignments', '', testUniqueAddOnAssignments),
});
type AutomaticAddOnsDTO = yup.InferType<typeof automaticAddOnsSchema>;

type AutomaticAddOnsHandler = {
  onSubmit: () => Promise<AutomaticAddOnsDTO>;
  isValid: () => boolean;
  isDirty: () => boolean;
};

interface AutomaticAddOnsProps {
  initialAddOns?: AddOnAssignment[];
  sx?: SxProps<Theme>;
}

export const constructDefaultValues = (initialAddOns?: AddOnAssignment[]) => {
  return {
    addOns: initialAddOns?.map((addOn) => ({
      isDeleted: false,
      isNew: false,
      addOn: {
        id: addOn.id,
        name: addOn.name,
        rateType: addOn.revenueCostRateType ?? '',
        quantity: addOn.quantity,
        rate: addOn.rate ?? undefined,
        percentage: addOn.percentage,
        externalId: addOn.externalId,
        accountingId: addOn.accountingId,
        rateValue:
          addOn.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
            ? addOn.percentage
            : (addOn.rate ?? undefined),
        costRateValue: addOn.costRateValue ?? undefined,
        addOnTypes: addOnTypeOptions,
        // Populate addOnId with the AddOn catalog ID so the dedup key collides
        // correctly with freshly-selected rows. New/existing distinction for
        // the save logic is carried by `isNew` (above), not by addOnId.
        addOnId: addOn.addOnId ?? '',
        fuelSurchargeSchedule: {
          id: addOn.addOn?.fuelSurchargeSchedule?.id ?? '',
          name: addOn.addOn?.fuelSurchargeSchedule?.name ?? '',
        },
      },
    })) ?? [{ isDeleted: false, isNew: true, addOn: constructEmptyAddOn() }],
  };
};

const AutomaticAddOns = forwardRef<AutomaticAddOnsHandler, AutomaticAddOnsProps>(
  function AutomaticAddOns({ initialAddOns, sx }, ref) {
    const { createAddOnByCompanyId, getAddOn } = useAddOns();
    const { toasterStore, userStore } = useStores();
    const navigate = useNavigate();
    const { getFuelSurchargeSchedulesTypeahead } = useFuelSurchargeSchedule();

    const companyId = getEffectiveUserCompanyId(userStore);

    const {
      control,
      formState: { errors },
      handleSubmit,
      setValue,
      reset,
      trigger,
      watch,
    } = useForm<AutomaticAddOnsDTO>({
      resolver: yupResolver(automaticAddOnsSchema),
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: constructDefaultValues(initialAddOns),
    });

    useEffect(() => {
      reset(constructDefaultValues(initialAddOns));
    }, [initialAddOns, reset]);

    // Cross-row dedup test lives on the array. RHF's onChange only re-validates
    // the changed field's own path, so a Cost/Invoice/Qty edit on one row
    // wouldn't refresh the array test on its own — subscribe and trigger.
    useEffect(() => {
      const subscription = watch((_, { name, type }) => {
        if (type === 'change' && name?.startsWith('addOns')) {
          trigger('addOns');
        }
      });
      return () => subscription.unsubscribe();
    }, [watch, trigger]);

    const list = useFieldArray({ control: control, name: 'addOns' });

    const addOnFormRef = useRef<AddOnFormHandler>({});
    const [addOnDialogState, setAddOnDialogState] = useState<AddOnDialogState>({
      isOpen: false,
    });

    const handleAddOnInput = useCallback((value: string, indexInList: number) => {
      setAddOnDialogState((state) => ({
        ...state,
        defaultAddOnName: value,
        indexInList,
      }));
    }, []);

    const handleCreateNewAddOnChipClick = useCallback((indexInList: number) => {
      setAddOnDialogState((state) => ({
        ...state,
        isOpen: true,
        indexInList,
        defaultAddOnName:
          state.indexInList === indexInList ? state.defaultAddOnName : undefined,
      }));
    }, []);

    const closeAddOnDialog = useCallback(() => {
      setAddOnDialogState({ isOpen: false });
    }, []);

    const fetchAddOns = useCallback(
      async (additionalProps: { query?: string; cursor?: string } = {}) => {
        try {
          const response = await getV1CompaniesCompanyIdAddOnsTypeahead({
            path: { 'company-id': companyId },
            query: {
              'search[query]': additionalProps.query || undefined,
              'page[limit]': DEFAULT_API_PAGE_SIZE,
              ...(additionalProps.cursor
                ? { 'page[after]': additionalProps.cursor }
                : {}),
            },
          });

          const addOns = response.data.data.map((addOn) => {
            const parsed = AddOnTypeahead.parse(addOn);

            return { id: parsed.id, name: parsed.name };
          });

          return { data: addOns, pagination: extractPagination(response) };
        } catch (error) {
          connection.handleRequestError(
            error,
            t('error_messages.add_ons.failed_to_fetch'),
          );
        }
      },
      [companyId],
    );

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve, reject) => {
            handleSubmit(
              (data) => {
                const prunedList = {
                  addOns: data.addOns.reduce(
                    (acc, addOn) => {
                      const isValid = Boolean(addOn.addOn?.id);
                      if (isValid) {
                        acc.push(addOn);
                      }
                      return acc;
                    },
                    [] as typeof data.addOns,
                  ),
                };
                resolve(prunedList);
              },
              (errors) => {
                reject(errors);
              },
            )();
          });
        },
        isValid() {
          return Object.keys(errors).length === 0;
        },
        isDirty() {
          return list.fields.some((field, index) => {
            const currentValue = list.fields[index];
            const initialValue = initialAddOns?.[index];
            return (
              !initialValue ||
              JSON.stringify(currentValue) !== JSON.stringify(initialValue)
            );
          });
        },
      }),
      [handleSubmit, errors, list.fields, initialAddOns],
    );

    const fillAddOnDetails = useCallback(
      async (
        item: NameIdSchemaRequired | null,
        index: number,
        prefetchedAddOn?: AddOn,
      ) => {
        if (item?.id) {
          // Capture the original add-on ID before it gets overwritten
          const currentField = list.fields[index];
          const originalAddOnId = currentField?.addOn?.id;
          const isReplacingExistingAddOn =
            originalAddOnId && originalAddOnId !== item.id && !currentField?.isNew;

          const addOn = prefetchedAddOn ?? (await getAddOn(companyId, item.id));

          if (addOn) {
            // Mark the original add-on for deletion if we're replacing an existing one
            if (isReplacingExistingAddOn) {
              list.append({
                isDeleted: true,
                addOn: {
                  id: originalAddOnId,
                  fuelSurchargeSchedule: { id: '', name: '' },
                },
              });
            }

            // Update all values at once using list.update
            list.update(index, {
              isDeleted: false,
              isNew: true,
              addOn: {
                id: addOn.id,
                name: addOn.name,
                rateType: addOn.revenueCostRateType,
                quantity: addOn.quantity,
                percentage: addOn.percentage,
                externalId: addOn.externalId ?? '',
                accountingId: addOn.accountingId ?? '',
                rateValue:
                  addOn.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
                    ? addOn.percentage
                    : (addOn.rate ?? undefined),
                costRateValue: addOn.costRateValue ?? undefined,
                addOnTypes: addOnTypeOptions,
                addOnId: addOn.id,
                fuelSurchargeSchedule: {
                  id: addOn.fuelSurchargeSchedule?.id ?? '',
                  name: addOn.fuelSurchargeSchedule?.name ?? '',
                },
              },
            });
          }

          return {
            id: list.fields[index].addOn?.id || addOn?.id,
            name: addOn?.name,
            rateType: addOn?.revenueCostRateType,
            quantity: addOn?.quantity,
            rate: addOn?.rate ?? undefined,
            percentage: addOn?.percentage,
            externalId: addOn?.externalId,
            accountingId: addOn?.accountingId,
            rateValue:
              addOn?.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
                ? addOn?.percentage
                : (addOn?.rate ?? undefined),
            costRateValue: addOn?.costRateValue ?? undefined,
            addOnTypes: addOnTypeOptions,
            addOnId: addOn?.id,
            fuelSurchargeSchedule: addOn?.fuelSurchargeSchedule,
          };
        }

        return item;
      },
      [companyId, list.fields],
    );

    const handleAddOnFormSubmit = useCallback(async () => {
      const data = await addOnFormRef.current?.onSubmit?.();
      const index = addOnDialogState.indexInList;

      if (!data || isNil(index)) {
        return;
      }

      setAddOnDialogState((state) => ({ ...state, isSaving: true }));

      try {
        const newAddOn = await createAddOnByCompanyId(companyId, data);

        await fillAddOnDetails(
          { id: newAddOn.id ?? '', name: newAddOn.name },
          index,
          newAddOn,
        );

        toasterStore.push(
          alert(t('administration.rates.add_ons.add_on_created'), AlertTypes.success),
        );
        closeAddOnDialog();
      } catch {
        setAddOnDialogState((state) => ({ ...state, isSaving: undefined }));
      }
    }, [
      addOnDialogState.indexInList,
      closeAddOnDialog,
      companyId,
      createAddOnByCompanyId,
      fillAddOnDetails,
      toasterStore,
    ]);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...sx }}>
        {list.fields.map((item, index) => {
          if (item.isDeleted) {
            return null;
          }

          return (
            <Box
              key={item.id}
              sx={{
                py: 1.5,
                position: 'relative',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <RemoveItemButton
                action={() => {
                  list.update(index, {
                    addOn: {
                      id: item.addOn?.id ?? '',
                      fuelSurchargeSchedule: { id: '', name: '' },
                    },
                    isDeleted: true,
                    isNew: item.isNew,
                  });
                }}
                sx={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-10px',
                  cursor: 'pointer',
                }}
              />
              <Grid container spacing={2}>
                <Grid size={{ xs: 4 }}>
                  <AutocompleteAsyncFormField
                    isRequired
                    disablePortal={false}
                    asyncCallback={fetchAddOns}
                    clearable
                    control={control}
                    errors={errors}
                    getLabel={(item: NameIdSchemaRequired | null) =>
                      item?.id ? item?.name : ''
                    }
                    getValue={(item: NameIdSchemaRequired | null) => item?.id ?? ''}
                    label={t('administration.automatic_settlement_add_ons.add_on')}
                    name={`addOns.${index}.addOn`}
                    noOptionsText={
                      <Chip
                        label={t('administration.rates.automatic_add_ons.new_add_on')}
                        onClick={() => handleCreateNewAddOnChipClick(index)}
                        size="small"
                        sx={{ cursor: 'pointer' }}
                      />
                    }
                    onClear={() => {
                      const emptyAddOn = constructEmptyAddOn();

                      // Mark the original add-on for deletion if it exists and was persisted
                      if (item.addOn?.id && !item.isNew) {
                        list.append({
                          isDeleted: true,
                          addOn: {
                            id: item.addOn.id,
                            fuelSurchargeSchedule: { id: '', name: '' },
                          },
                        });
                      }

                      // Clear the current row's form fields so user can select a new add-on
                      setValue(`addOns.${index}.addOn`, emptyAddOn);
                      setValue(`addOns.${index}.addOn.quantity`, emptyAddOn.quantity);
                      setValue(`addOns.${index}.addOn.rateType`, emptyAddOn.rateType);
                      setValue(`addOns.${index}.addOn.rateValue`, emptyAddOn.rateValue);
                      setValue(
                        `addOns.${index}.addOn.costRateValue`,
                        emptyAddOn.costRateValue,
                      );
                      setValue(`addOns.${index}.addOn.addOnTypes`, emptyAddOn.addOnTypes);
                      setValue(`addOns.${index}.addOn.addOnId`, emptyAddOn.addOnId);
                      setValue(`addOns.${index}.addOn.fuelSurchargeSchedule`, {
                        id: '',
                        name: '',
                      });
                    }}
                    onInput={(_event, value) => handleAddOnInput(value, index)}
                    onSelect={(item) => fillAddOnDetails(item, index)}
                  />
                </Grid>

                <Grid size={{ xs: 2 }}>
                  <AutocompleteFormField
                    isRequired
                    control={control}
                    errors={errors}
                    getLabel={(item: AddOnRateType) => getAddOnRateTypeOptionLabel(item)}
                    getValue={(item: AddOnRateType) => item}
                    label={`${t('administration.rates.automatic_add_ons.rate_type')}`}
                    list={addOnRateTypeOptionsList}
                    name={`addOns.${index}.addOn.rateType`}
                    dataUi="automatic-add-on-row__rate-type"
                    disablePortal={false}
                    disabled={item.addOn?.rateType === AddOnRateType.FUEL_SURCHARGE}
                  />
                </Grid>

                {item.addOn?.rateType !== AddOnRateType.FUEL_SURCHARGE && (
                  <>
                    <Grid size={{ xs: 2 }}>
                      <TextFormField
                        control={control}
                        errors={errors}
                        label={`${t('administration.rates.add_ons.cost_rate')}`}
                        name={`addOns.${index}.addOn.costRateValue`}
                        type="number"
                      />
                    </Grid>

                    <Grid size={{ xs: 2 }}>
                      <TextFormField
                        control={control}
                        errors={errors}
                        label={`${t('administration.rates.invoice_rate')}`}
                        name={`addOns.${index}.addOn.rateValue`}
                        type="number"
                      />
                    </Grid>

                    <Grid size={{ xs: 2 }}>
                      <TextFormField
                        control={control}
                        errors={errors}
                        label={`${t('administration.rates.automatic_add_ons.quantity')}`}
                        name={`addOns.${index}.addOn.quantity`}
                        type="number"
                      />
                    </Grid>
                  </>
                )}

                {item.addOn?.rateType === AddOnRateType.FUEL_SURCHARGE && (
                  <>
                    <Grid size={{ xs: 6 }}>
                      <AutocompleteAsyncFormField
                        clearable
                        control={control}
                        errors={errors}
                        label={`${t('administration.rates.add_ons.fuel_surcharge_schedule')}`}
                        name={`addOns.${index}.addOn.fuelSurchargeSchedule`}
                        asyncCallback={async (options: any) => {
                          const result = await getFuelSurchargeSchedulesTypeahead({
                            searchQuery: options.query,
                            cursor: options.cursor,
                          });
                          return result;
                        }}
                        getValue={(item: any) => item?.id || ''}
                        getLabel={(item: any) => item?.name || ''}
                        disabled
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" sx={{ mt: -2 }}>
                        {t(
                          'administration.rates.add_ons.fuel_surcharge_schedule_subtext',
                        )}
                        <Button
                          variant="text"
                          size="small"
                          onClick={() =>
                            navigate(
                              `/${ROUTES.settings.base}/${ROUTES.settings.fuelSurchargeManagement}`,
                            )
                          }
                          sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                        >
                          {t('page_headings.settings')}
                        </Button>
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          );
        })}
        <Box>
          <PseudoLink
            title={`+ ${t('common.add_another')}`}
            action={() =>
              list.append({
                isDeleted: false,
                isNew: true,
                addOn: constructEmptyAddOn(),
              })
            }
            sx={{ display: 'inline-flex' }}
          />
        </Box>
        <Dialog
          isOpen={addOnDialogState.isOpen}
          onClose={closeAddOnDialog}
          preventCloseOnBackdropClick
          sx={{ [`& .${paperClasses.root}`]: { maxWidth: '700px' } }}
        >
          <DialogHeader onClose={closeAddOnDialog}>
            {t('administration.rates.add_ons.create_add_on')}
          </DialogHeader>

          <DialogContent>
            <AddOnForm
              ref={addOnFormRef}
              defaultAddOnName={addOnDialogState.defaultAddOnName}
            />
          </DialogContent>

          <DialogActions>
            <SmallButton
              color="secondary"
              disabled={addOnDialogState.isSaving}
              onClick={closeAddOnDialog}
              variant="outlined"
            >
              {t('actions.cancel')}
            </SmallButton>

            <SmallLoadingButton
              loading={addOnDialogState.isSaving}
              onClick={handleAddOnFormSubmit}
            >
              {t('actions.create')}
            </SmallLoadingButton>
          </DialogActions>
        </Dialog>
      </Box>
    );
  },
);

export default AutomaticAddOns;
